import React, { useState, useMemo, useEffect } from "react";
import Header from "../../Components/Header/Header";
import VideoComments from "../VideoComments/VideoComments";
import "./CommentModeration.css";
import PaginationLeft from "../../assets/images/pagination-left-arrow.svg";
import PaginationRight from "../../assets/images/pagination-right-arrow.svg";
import { callApi } from "../../utils/apiService";
import { videoCreatorEndPoint } from "../../Constants/constants";
import dayjs from "dayjs";

const CommentModeration = () => {
  const [activeTab, setActiveTab] = useState("total");
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const ITEMS_PER_PAGE = 10;

  const [stats, setStats] = useState({
    totalComments: 0,
    todayComments: 0,
    unreadComments: 0,
  });

  const [commentsData, setCommentsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  const showingFrom =
    totalItems === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;

  const showingTo = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

  const fetchCommentStats = async () => {
    try {
      const response = await callApi(
        `${videoCreatorEndPoint}video/comment/dashboard/stats`,
        "GET",
      );

      if (response?.status) {
        setStats(response?.data || {});
      }
    } catch (error) {
      console.error("Comment Stats API Error:", error);
    }
  };

  const fetchCommentsList = async () => {
    try {
      setLoading(true);

      const typeMap = {
        total: "TOTAL",
        today: "TODAY",
        unread: "UNREAD",
      };

      const queryParams = new URLSearchParams({
        type: typeMap[activeTab],
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      });

      const response = await callApi(
        `${videoCreatorEndPoint}video/comment/dashboard/list?${queryParams}`,
        "GET",
      );

      if (response?.status) {
        setCommentsData(response?.data?.comments || []);
        setTotalItems(response?.data?.pagination?.totalItems || 0);
        setTotalPages(response?.data?.pagination?.totalPages || 0);
      }
    } catch (error) {
      console.error("Comment List API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommentStats();
  }, []);

  useEffect(() => {
    fetchCommentsList();
  }, [activeTab, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const totalComments = stats?.totalComments || 0;
  const todayComments = stats?.todayComments || 0;
  const unreadComments = stats?.unreadComments || 0;

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };
  const handlePageChange = (p) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };

  const getPaginationButtons = () => {
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  // Show VideoComments detail page
  if (selectedVideoId !== null) {
    return (
      <VideoComments
        videoId={selectedVideoId}
        onBack={() => setSelectedVideoId(null)}
      />
    );
  }

  return (
    <div className="comment-moderation-page">
      <Header />

      <div className="comments-container">
        <div className="comments-header">
          <h1>All Comments</h1>
          <p>Manage comments across all your videos</p>
        </div>

        <div className="stats-cards-grid">
          {[
            { tab: "total", label: "Total Comments", count: totalComments },
            { tab: "today", label: "Today Comments", count: todayComments },
            {
              tab: "unread",
              label: "Unread Comments",
              count: unreadComments,
              extra: "unread-card",
            },
          ].map(({ tab, label, count, extra }) => (
            <div
              key={tab}
              className={`stat-card ${extra || ""} ${activeTab === tab ? "active" : ""}`}
              onClick={() => handleTabClick(tab)}
              style={{ cursor: "pointer" }}
            >
              <h3>{label}</h3>
              <p className="stat-number">{count}</p>
            </div>
          ))}
        </div>

        <div className="comments-table-section">
          <table className="comments-table">
            <thead>
              <tr>
                <th>Video Title</th>
                <th>Total Comments</th>
                <th>Latest Time Stamp</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {commentsData.length > 0 ? (
                commentsData.map((item) => (
                  <tr key={item.videoId}>
                    <td className="video-title">{item.videoTitle}</td>
                    <td>{item.totalComments} Comments</td>
                    <td className="comments-date">
                      {dayjs(item.latestCommentDate).format(
                        "DD MMM, YYYY hh:mm A",
                      )}
                    </td>
                    <td>
                      <button
                        className={`view-comments-btn ${item.isUnread ? "unread" : "read"}`}
                        onClick={() => setSelectedVideoId(item.videoId)}
                      >
                        View Comments
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="no-data">
                    No comments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="pagination-section">
            <div className="pagination-info">
              Showing{" "}
              <span className="pagination-num">
                {showingFrom}-{showingTo}
              </span>{" "}
              from <span className="pagination-num">{totalItems}</span> data
            </div>
            <div className="pagination-controls">
              <img
                src={PaginationLeft}
                alt="Left Arrow"
                style={{
                  width: "32px",
                  height: "32px",
                  cursor: currentPage > 1 ? "pointer" : "not-allowed",
                  opacity: currentPage > 1 ? 1 : 0.5,
                }}
                onClick={() =>
                  currentPage > 1 && handlePageChange(currentPage - 1)
                }
              />
              <div className="pagination-numbers">
                {getPaginationButtons().map((p) => (
                  <button
                    key={p}
                    className={`pagination-number ${currentPage === p ? "active" : ""}`}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <img
                src={PaginationRight}
                alt="Right Arrow"
                style={{
                  width: "32px",
                  height: "32px",
                  cursor: currentPage < totalPages ? "pointer" : "not-allowed",
                  opacity: currentPage < totalPages ? 1 : 0.5,
                }}
                onClick={() =>
                  currentPage < totalPages && handlePageChange(currentPage + 1)
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentModeration;
